/**
 * JobMockAI — App Shell Layout
 *
 * Wraps all authenticated pages with:
 *  - Persistent sidebar (collapsible drawer on mobile)
 *  - Top bar with notification bell and user menu
 *  - Main scrollable content area
 *
 * Usage: wrap an <Outlet /> from react-router-dom inside this layout.
 */
import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import Sidebar from '../components/layout/Sidebar';
import TopBar from '../components/layout/TopBar';
import PostAuthRedirectNotice from '../components/PostAuthRedirectNotice';
import CommunityUploadStatusBar from '../components/community/CommunityUploadStatusBar';
import { shellContentAreaSx } from './shellContentAreaSx';

/** Community feed + feed-first dashboard need edge-to-edge media on phones. */
function isMobileFlushPath(pathname) {
  const path = pathname || '';
  return path === '/' || /^\/community\/feed\/?$/.test(path);
}

const AppShell = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  const flushMobile = isMobileFlushPath(pathname);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        '@supports (height: 100dvh)': { height: '100dvh' },
      }}
    >
      <PostAuthRedirectNotice />
      {/* Sidebar */}
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main content column */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // prevents flex child from overflowing
          minHeight: 0, // allow nested overflowY:auto to scroll
        }}
      >
        {/* Top bar */}
        <TopBar onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />

        {/* Page content — full main-column width; Containers stretch inside the shell */}
        <Box
          sx={{
            ...shellContentAreaSx,
            ...(flushMobile
              ? {
                  // Feed/home: zero shell gutters so the center column can go edge-to-edge
                  px: { xs: 0, sm: 0 },
                  pt: 0,
                  pb: { xs: 1.5, sm: 2 },
                  overflowX: 'clip',
                }
              : null),
          }}
          data-testid={flushMobile ? 'app-shell-content-flush' : 'app-shell-content'}
        >
          <Outlet />
        </Box>
      </Box>
      <CommunityUploadStatusBar />
    </Box>
  );
};

export default AppShell;
